/**
 * THE MAGNET SUCK — screen-space coin sprites flying to the HUD counter.
 *
 * The Temple Run / Subway Surfers moment: the instant a coin is collected the
 * world instance disappears and a small gold sprite peels off its last screen
 * position and arcs into the coin counter, which ticks up as it lands.
 *
 * WHY IT IS DOM, AND WHY IT IS RENDER-ONLY. The pickup is already final in sim
 * state the step it happens (sim/coins.ts) — this animation cannot affect it,
 * so a dropped frame or a mid-flight pause can never cost the player a coin.
 * Doing it in the DOM (like render/popups.ts) rather than in the 3D scene keeps
 * it out of the render pipeline entirely: no new material, no new geometry, no
 * pipeline to compile mid-run, and it composites over the letterboxed 9:16 HUD
 * in exactly the same coordinate space as the counter it is flying to.
 *
 * POOLED, like popups: a fixed ring of `<div>`s is created once and recycled.
 * A chain of ten coins collected over ~1.5 s puts at most a handful in the air
 * at once; the pool is sized for a whole chain plus overlap and the ring simply
 * recycles the oldest if it is ever exceeded.
 *
 * THE CURVE. A straight line reads as a UI element sliding; the coin should
 * read as being PULLED. Each sprite gets a fixed sideways bow (alternating
 * left/right per spawn so a stream fans out instead of stacking) applied as a
 * quadratic offset perpendicular to the travel direction, and eases
 * out-then-in: it hangs for a moment where the coin was, then accelerates into
 * the counter. The scale shrinks slightly on the way so the arrival reads as
 * the counter absorbing it.
 *
 * ZERO steady-state allocation: `update` writes `transform`/`opacity` on the
 * pooled elements; the only per-pickup work is filling in a slot's numbers.
 */

/** How long a sprite takes to reach the counter (s). */
export const FLIGHT_DURATION = 0.35;
/** Pooled sprites. A ten-coin chain plus overlap fits comfortably. */
const POOL_SIZE = 24;
/** Sprite diameter in CSS px. */
const SPRITE_PX = 20;
/** Peak sideways bow, as a fraction of the flight distance. */
const BOW_FRACTION = 0.22;

interface FlightSlot {
  el: HTMLDivElement;
  active: boolean;
  elapsed: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** Perpendicular bow amplitude in px, signed. */
  bow: number;
  /** True once the arrival callback has fired for this flight. */
  landed: boolean;
}

/**
 * Ease for the flight parameter: slow out of the pickup, hard into the counter.
 * Pure, so it is unit-testable and so the curve can be retuned without touching
 * the DOM code.
 */
export function flightEase(t: number): number {
  // Cubic ease-in with a small linear head — the sprite lingers for ~15% of the
  // flight (long enough for the eye to connect it to the coin that vanished)
  // and then snaps into the counter.
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  return c * c * c * 0.85 + c * 0.15;
}

/**
 * The coin-flight overlay. Construct with the HUD root (the 9:16 viewport
 * container); call `spawn` on each `coin` sim event and `update(dt)` every
 * render frame.
 */
export class CoinFlight {
  private readonly slots: FlightSlot[] = [];
  private nextIdx = 0;
  /** Alternates the bow direction so a stream fans instead of stacking. */
  private bowSign = 1;
  /** Called each time a sprite lands — the HUD ticks its counter here. */
  private onArrive: (() => void) | null = null;

  constructor(root: HTMLDivElement) {
    for (let i = 0; i < POOL_SIZE; i++) {
      const el = document.createElement("div");
      Object.assign(el.style, {
        position: "absolute",
        width: `${SPRITE_PX}px`,
        height: `${SPRITE_PX}px`,
        borderRadius: "50%",
        // A gold disc with a highlight — the same read as the 3D coin, at 20 px.
        background:
          "radial-gradient(circle at 34% 30%, #fff3c4 0%, #ffc93c 45%, #d98f14 100%)",
        boxShadow: "0 0 8px rgba(255,201,60,0.75)",
        border: "1px solid rgba(120,72,0,0.55)",
        pointerEvents: "none",
        transform: "translate(-50%, -50%)",
        opacity: "0",
        display: "none",
        willChange: "transform, opacity",
        zIndex: "12",
      } as Partial<CSSStyleDeclaration>);
      root.appendChild(el);
      this.slots.push({
        el,
        active: false,
        elapsed: 0,
        x0: 0,
        y0: 0,
        x1: 0,
        y1: 0,
        bow: 0,
        landed: false,
      });
    }
  }

  /** Register the counter tick. Called once at wiring time. */
  setArrivalHandler(fn: () => void): void {
    this.onArrive = fn;
  }

  /**
   * Launch one sprite from a screen position toward the counter.
   *
   * @param x0,y0 pickup position in viewport px (projected from the world).
   * @param x1,y1 the counter's position in viewport px.
   */
  spawn(x0: number, y0: number, x1: number, y1: number): void {
    const s = this.slots[this.nextIdx];
    this.nextIdx = (this.nextIdx + 1) % this.slots.length;
    s.active = true;
    s.landed = false;
    s.elapsed = 0;
    s.x0 = x0;
    s.y0 = y0;
    s.x1 = x1;
    s.y1 = y1;
    const dist = Math.hypot(x1 - x0, y1 - y0);
    s.bow = this.bowSign * BOW_FRACTION * dist;
    this.bowSign = -this.bowSign;
    s.el.style.display = "block";
    s.el.style.opacity = "1";
    s.el.style.transform = `translate(${x0}px, ${y0}px) translate(-50%, -50%)`;
  }

  /** True while any sprite is in the air (tests / QA introspection). */
  get activeCount(): number {
    let n = 0;
    for (const s of this.slots) if (s.active) n++;
    return n;
  }

  /** Advance every sprite by `dt` seconds. */
  update(dt: number): void {
    for (let i = 0; i < this.slots.length; i++) {
      const s = this.slots[i];
      if (!s.active) continue;
      s.elapsed += dt;
      const raw = s.elapsed / FLIGHT_DURATION;
      if (raw >= 1) {
        if (!s.landed) {
          s.landed = true;
          this.onArrive?.();
        }
        s.active = false;
        s.el.style.display = "none";
        s.el.style.opacity = "0";
        continue;
      }
      const e = flightEase(raw);
      const dx = s.x1 - s.x0;
      const dy = s.y1 - s.y0;
      const len = Math.hypot(dx, dy) || 1;
      // Perpendicular unit vector, bowed by a parabola that is 0 at both ends.
      const px = -dy / len;
      const py = dx / len;
      const bow = s.bow * 4 * e * (1 - e);
      const x = s.x0 + dx * e + px * bow;
      const y = s.y0 + dy * e + py * bow;
      // Shrink into the counter, and fade only at the very end so the sprite
      // stays legible for almost the whole flight.
      const scale = 1 - 0.45 * e;
      const opacity = raw > 0.82 ? (1 - raw) / 0.18 : 1;
      s.el.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%) scale(${scale.toFixed(3)})`;
      s.el.style.opacity = opacity.toFixed(3);
    }
  }

  /** Cancel every flight (phase transitions / restart). */
  reset(): void {
    for (const s of this.slots) {
      s.active = false;
      s.landed = true;
      s.el.style.display = "none";
      s.el.style.opacity = "0";
    }
  }

  dispose(): void {
    for (const s of this.slots) s.el.remove();
    this.slots.length = 0;
  }
}
